/**
 * JurisdictionAuditDrawer
 * =======================
 *
 * Right-side slide-in drawer that shows the Activity Timeline / Audit Trail
 * for a single jurisdiction record. Powered by GET /api/jurisdictions/{id}/audit-log.
 *
 * Spec (Feb 22, 2026 — Session 19):
 *   • Width 440px on desktop, full-width on mobile
 *   • Sticky header with jurisdiction name + last-updated info + close button
 *   • Timeline with left vertical rail, circular marker per event, card block
 *   • Color-coded action badges: green=create, blue=update, amber=toggle
 *   • Field-level diff (from → to); if >3 fields changed, show "Updated N fields ▾"
 *   • Empty state: "No activity yet"
 *   • Read-only (no edit / delete possible from UI)
 *
 * Access: backend already restricts GET to `admin` | `jurisdiction_manager`.
 * The parent component is responsible for only rendering the trigger for
 * those roles; this component trusts the API.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Clock,
  PlusCircle,
  Pencil,
  Power,
  PowerOff,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertCircle,
  Inbox,
  User as UserIcon,
} from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const ET_TIMEZONE = 'America/New_York';

// ─── Action-type metadata (single source for badge styles + icon + label) ──
const ACTION_META = {
  create: {
    label: 'Created',
    icon: PlusCircle,
    markerClass: 'bg-emerald-500 ring-emerald-100',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  update: {
    label: 'Updated',
    icon: Pencil,
    markerClass: 'bg-blue-500 ring-blue-100',
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  activate: {
    label: 'Activated',
    icon: Power,
    markerClass: 'bg-amber-500 ring-amber-100',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  deactivate: {
    label: 'Deactivated',
    icon: PowerOff,
    markerClass: 'bg-amber-500 ring-amber-100',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
};

// Fields considered "critical" — diff row gets a red left accent for emphasis.
const CRITICAL_FIELDS = new Set([
  'jurisdiction_name',
  'jurisdiction_type',
  'state',
  'parent_county_id',
  'parent_county_name',
  'tax_collector_name',
  'is_active',
]);

const INITIAL_VISIBLE_DIFF_ROWS = 3;

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatET = (iso) => {
  if (!iso) return '—';
  try {
    return formatInTimeZone(new Date(iso), ET_TIMEZONE, 'MMM d, yyyy — h:mm a');
  } catch {
    return iso;
  }
};

const formatShortET = (iso) => {
  if (!iso) return '—';
  try {
    return formatInTimeZone(new Date(iso), ET_TIMEZONE, "MMM d, h:mm a 'ET'");
  } catch {
    return iso;
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatDiffValue = (value) => {
  if (value === null || value === undefined || value === '') return <span className="italic text-slate-400">empty</span>;
  if (value === true) return 'Active';
  if (value === false) return 'Inactive';
  return String(value);
};

// ─── Diff block (collapsible when many fields) ─────────────────────────────
const DiffRows = ({ changes }) => {
  const [expanded, setExpanded] = useState(false);
  const entries = Object.entries(changes || {});

  if (entries.length === 0) {
    return null;
  }

  const visibleEntries = expanded ? entries : entries.slice(0, INITIAL_VISIBLE_DIFF_ROWS);
  const hiddenCount = entries.length - INITIAL_VISIBLE_DIFF_ROWS;

  return (
    <div className="space-y-2 mt-3">
      {visibleEntries.map(([field, diff]) => {
        const critical = CRITICAL_FIELDS.has(field);
        return (
          <div
            key={field}
            className={`rounded-lg bg-slate-50 px-3 py-2 text-xs border-l-2 ${
              critical ? 'border-rose-300' : 'border-slate-200'
            }`}
            data-testid={`audit-diff-row-${field}`}
          >
            <div className="font-medium text-slate-700 mb-1">
              {diff.label || field}
            </div>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <span className="text-slate-400 uppercase tracking-wide text-[10px]">From</span>
                <div className="text-slate-600 break-words">{formatDiffValue(diff.from)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-slate-400 uppercase tracking-wide text-[10px]">To</span>
                <div className="text-slate-900 font-medium break-words">{formatDiffValue(diff.to)}</div>
              </div>
            </div>
          </div>
        );
      })}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
          data-testid="audit-diff-toggle"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" /> {hiddenCount} more field{hiddenCount === 1 ? '' : 's'}
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ─── Single timeline event card ────────────────────────────────────────────
const TimelineEvent = ({ event, isLast, isRecent }) => {
  const meta = ACTION_META[event.action_type] || ACTION_META.update;
  const Icon = meta.icon;
  const changeCount = Object.keys(event.changes || {}).length;

  return (
    <div className="relative pl-10 pb-6" data-testid={`audit-event-${event.id}`}>
      {/* Vertical rail */}
      {!isLast && (
        <span
          className="absolute left-[15px] top-7 bottom-0 w-px bg-slate-200"
          aria-hidden
        />
      )}
      {/* Marker */}
      <span
        className={`absolute left-1 top-1 w-6 h-6 rounded-full ring-4 flex items-center justify-center text-white ${meta.markerClass}`}
        aria-hidden
      >
        <Icon className="w-3 h-3" />
      </span>

      {/* Card */}
      <div
        className={`rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow transition-all p-4 ${
          isRecent ? 'ring-2 ring-blue-200 animate-pulse-once' : ''
        }`}
      >
        {/* Top row — user + timestamp + action badge */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-[11px] font-semibold flex items-center justify-center"
              title={event.changed_by_name}
            >
              {getInitials(event.changed_by_name)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                {event.changed_by_name || 'Unknown user'}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatET(event.changed_at)}
              </div>
            </div>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.badgeClass}`}
          >
            <Icon className="w-3 h-3" /> {meta.label}
          </span>
        </div>

        {/* Summary line */}
        {event.action_type === 'create' && (
          <p className="text-xs text-slate-600">
            Created this jurisdiction with {changeCount} initial field{changeCount === 1 ? '' : 's'}.
          </p>
        )}
        {event.action_type === 'update' && (
          <p className="text-xs text-slate-600">
            Updated {changeCount} field{changeCount === 1 ? '' : 's'}.
          </p>
        )}
        {event.action_type === 'activate' && (
          <p className="text-xs text-slate-600">Marked active.</p>
        )}
        {event.action_type === 'deactivate' && (
          <p className="text-xs text-slate-600">Marked inactive.</p>
        )}

        {/* Field-level diff */}
        <DiffRows changes={event.changes} />
      </div>
    </div>
  );
};

// ─── Main drawer ───────────────────────────────────────────────────────────
const JurisdictionAuditDrawer = ({ jurisdictionId, jurisdictionName, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchAuditLog = useCallback(async () => {
    if (!jurisdictionId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${API_BASE_URL}/api/jurisdictions/${jurisdictionId}/audit-log`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to load activity (${resp.status})`);
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [jurisdictionId]);

  useEffect(() => {
    if (isOpen && jurisdictionId) fetchAuditLog();
  }, [isOpen, jurisdictionId, fetchAuditLog]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Highlight events that happened in the last 3 seconds (fresh updates).
  // Recalculate cutoff when data changes (new fetch) so fresh events pulse.
  const recentCutoff = useMemo(() => Date.now() - 3000, [data?.total]);
  const isEventRecent = (iso) => {
    try {
      return new Date(iso).getTime() > recentCutoff;
    } catch {
      return false;
    }
  };

  if (!isOpen) return null;

  const events = data?.events || [];
  const hasEvents = events.length > 0;

  const drawer = (
    <div
      className="fixed inset-0 z-[60] flex"
      data-testid="jurisdiction-audit-drawer"
      aria-labelledby="audit-drawer-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px] animate-in fade-in duration-200"
        onClick={onClose}
        data-testid="audit-drawer-backdrop"
      />
      {/* Drawer panel */}
      <div
        className="ml-auto w-full sm:w-[440px] lg:w-[480px] h-full bg-[#F9FAFB] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
        role="document"
      >
        {/* Sticky header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2
                id="audit-drawer-title"
                className="text-base font-semibold text-slate-900 truncate"
                title={jurisdictionName || data?.jurisdiction_name}
              >
                {jurisdictionName || data?.jurisdiction_name || 'Activity Timeline'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {data?.jurisdiction_type ? (
                  <span className="capitalize">{data.jurisdiction_type}</span>
                ) : null}
                {data?.state ? ` • ${data.state}` : null}
              </p>
              {data?.last_updated_by_name && (
                <p className="text-xs text-slate-600 mt-1.5 flex items-center gap-1">
                  <UserIcon className="w-3 h-3 text-slate-400" />
                  <span className="font-medium">Last updated by</span>
                  <span>{data.last_updated_by_name}</span>
                  <span className="text-slate-400">•</span>
                  <span>{formatShortET(data.last_updated_at)}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={fetchAuditLog}
                className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                title="Refresh"
                data-testid="audit-drawer-refresh"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                title="Close"
                data-testid="audit-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {loading && !data && (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              Loading activity…
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rose-700">{error}</p>
            </div>
          )}

          {!loading && !error && !hasEvents && (
            <div
              className="flex flex-col items-center justify-center py-16 text-center"
              data-testid="audit-drawer-empty"
            >
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <Inbox className="w-7 h-7 text-slate-400" />
              </div>
              <h3 className="text-sm font-semibold text-slate-700">No activity yet</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-[280px]">
                Changes to this jurisdiction will appear here going forward.
              </p>
            </div>
          )}

          {hasEvents && (
            <div data-testid="audit-drawer-timeline">
              {events.map((evt, idx) => (
                <TimelineEvent
                  key={evt.id}
                  event={evt}
                  isLast={idx === events.length - 1}
                  isRecent={isEventRecent(evt.changed_at)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer — read-only disclosure */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-slate-200 bg-white">
          <p className="text-[11px] text-slate-400 text-center">
            This timeline is read-only. All changes are logged automatically.
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
};

export default JurisdictionAuditDrawer;
